import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

/**
 * Keeps table state (search, filters, pagination, sorting) in sync with the
 * URL query string, so browser back navigation restores the previous view.
 */
export default function useUrlTableState({
  defaultStatus,
  defaultSortField,
  defaultSortOrder,
}) {
  const history = useHistory();
  const urlParams = new URLSearchParams(window.location.search);
  const statusParam = urlParams.get("status");

  const [searchText, setSearchText] = useState(urlParams.get("search") || "");
  const [pagination, setPagination] = useState({
    current: parseInt(urlParams.get("page"), 10) || 1,
    pageSize: parseInt(urlParams.get("size"), 10) || 10,
  });
  const [filters, setFilters] = useState({
    language: urlParams.get("language")?.split(",") || [],
    status: statusParam
      ? statusParam === "all"
        ? []
        : statusParam.split(",")
      : defaultStatus,
  });
  const [sorting, setSorting] = useState({
    field: urlParams.get("sortField") || defaultSortField,
    order: urlParams.get("sortOrder") || defaultSortOrder,
  });

  useEffect(() => {
    const queryParams = new URLSearchParams();
    if (searchText) queryParams.set("search", searchText);
    if (filters.language?.length)
      queryParams.set("language", filters.language.join(","));
    if (JSON.stringify(filters.status) !== JSON.stringify(defaultStatus)) {
      queryParams.set(
        "status",
        filters.status?.length ? filters.status.join(",") : "all",
      );
    }
    if (pagination.current !== 1) queryParams.set("page", pagination.current);
    if (pagination.pageSize !== 10)
      queryParams.set("size", pagination.pageSize);
    if (
      sorting.field !== defaultSortField ||
      sorting.order !== defaultSortOrder
    ) {
      queryParams.set("sortField", sorting.field);
      queryParams.set("sortOrder", sorting.order);
    }
    const newSearch = queryParams.toString();
    if (newSearch !== history.location.search.replace(/^\?/, "")) {
      history.replace({
        pathname: history.location.pathname,
        search: newSearch ? `?${newSearch}` : "",
      });
    }
  }, [searchText, filters, pagination, sorting]);

  return {
    searchText,
    setSearchText,
    filters,
    setFilters,
    pagination,
    setPagination,
    sorting,
    setSorting,
  };
}
